import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    auth,
    googleProvider,
    db,
    doc,
    setDoc,
    getDoc,
} from "./firebase";
import {
    signInWithEmailAndPassword,
    signInWithPopup,
    sendPasswordResetEmail,
} from "firebase/auth";
import {
    collection,
    addDoc,
    Timestamp
} from "firebase/firestore";
import { setPersistence, browserLocalPersistence, browserSessionPersistence } from "firebase/auth";
import './App.css';
import Turnstile from "react-turnstile";
import axios from "axios";

function Login() {
    const navigate = useNavigate();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [rememberMe, setRememberMe] = useState(true); // default to true
    const [loading, setLoading] = useState(false);
    const [token, setToken] = useState(null);
    const [verified, setVerified] = useState(false);
    const [resetToken, setResetToken] = useState(null);
    const [resetVerified, setResetVerified] = useState(false);
    const [showResetPopup, setShowResetPopup] = useState(false);
    const [resetEmail, setResetEmail] = useState("");
    const [resetStatus, setResetStatus] = useState(null);


    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const containsScript = (input) => /<script.*?>.*?<\/script>/gi.test(input);

    const validateForm = () => {
        if (!email || !validateEmail(email)) {
            setError("Please enter a valid email.");
            return false;
        }

        if (containsScript(email) || containsScript(password)) {
            setError("Input contains forbidden content.");
            return false;
        }

        if (!email.includes("@")) {
            setError("Please enter a valid email.");
            return false;
        }
        if (!email || !password) {
            setError("Please fill in both fields.");
            return;
        }
        // if (password.length < 6) {
        //     setError("Password must be at least 6 characters.");
        //     return false;
        // }
        return true;
    };

    const logLogin = async (uid) => {
        await addDoc(collection(db, "user_activity"), {
            uid: uid,
            last_login_date: Timestamp.now(),
        });
    };

    const handleEmailLogin = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        if (!validateForm()) return;

        try {
            await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            await logLogin(user.uid);

            navigate("/", { state: { loggedIn: true } });
        } catch (err) {
            console.error(err.message);
            setLoading(false);
            setError("Invalid email or password.");
        }
    };

    const handleGoogleSignIn = async () => {
        setError("");
        setLoading(true);
        try {
            await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
            const result = await signInWithPopup(auth, googleProvider);
            const user = result.user;

            const userRef = doc(db, "users", user.uid);
            const userSnap = await getDoc(userRef);

            if (!userSnap.exists()) {
                await setDoc(userRef, {
                    uid: user.uid,
                    name: user.displayName,
                    email: user.email,
                    type: "FREE",
                    balance: 300,
                    register_date: Timestamp.now(),
                });
            }

            await logLogin(user.uid);
            navigate("/", { state: { loggedIn: true } });
        } catch (err) {
            console.error(err.message);
            setLoading(false);
            setError("Google sign-in failed.");
        }
    };

    const handleResetPassword = async () => {
        setError("");

        if (!resetEmail) {
            setError("Please enter your email before requesting a reset.");
            return;
        }
        if (!resetVerified) {
            setError("Please verify you're human before resetting your password.");
            return;
        }
        try {
            await sendPasswordResetEmail(auth, resetEmail);
            alert("📧 A reset link has been sent to your email.");
        } catch (err) {
            switch (err.code) {
                case "auth/user-not-found":
                    setError("No user with this email exists.");
                    break;
                case "auth/invalid-email":
                    setError("Please enter a valid email.");
                    break;
                default:
                    setError("Something went wrong. Try again later."+err.code);
            }
        }
    };

    useEffect(() => {
        if (email || password) setError("");
    }, [email, password]);

    return (
        <div className="login-container">
            <div className="login-card">
                <img src="/scrub_logo.png" alt="Scrub Mail logo" className="login-logo" />
                <h2 className="login-title">Sign In</h2>

                <div className="login-socials">
                    <button onClick={handleGoogleSignIn} className="login-icon-btn"><img src="/graphic/web_light_sq_na@1x.png" alt="Google"/></button>
                    {/*<button className="login-icon-btn"><img src="/microsoft.svg" alt="Microsoft" /></button>
                    <button className="login-icon-btn"><img src="/github.svg" alt="GitHub" /></button>
                    <button className="login-icon-btn"><img src="/linkedin.svg" alt="LinkedIn" /></button>*/}
                </div>

                <div className="login-divider">OR SIGN IN USING EMAIL</div>

                <form onSubmit={handleEmailLogin} className="login-form">
                    <label>Email</label>
                    <input type="email"
                           className="login-input"
                           placeholder="enter your email"
                           value={email}
                           onChange={(e) => setEmail(e.target.value)} />
                    <label>Password</label>
                    <input type="password"
                           className="login-input"
                           placeholder="enter your password"
                           value={password}
                           onChange={(e) => setPassword(e.target.value)}/>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: "16px",  fontSize: "1.2rem" }}>
                        <input
                            type="checkbox"
                            id="rememberMe"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            style={{ marginRight: "8px",  fontSize: "1.6rem"}}
                        />
                        <label htmlFor="rememberMe">Remember Me</label>
                    </div>
                    <div className="turnstile-wrapper">
                        <Turnstile
                            sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                            onVerify={async (token) => {
                                setToken(token);
                                try {
                                    const response = await axios.post(process.env.REACT_APP_API_URL+"/verify-turnstile", {
                                        token,
                                    });
                                    setVerified(response.data.success);
                                } catch (err) {
                                    console.error("Failed Turnstile check:", err);
                                    setVerified(false);
                                }
                            }}
                        />
                    </div>
                    <p></p>
                    <button type="submit" className="login-submit-btn"   disabled={!verified || loading}>  {loading ? "SIGNING IN..." : !verified ? "PLEASE COMPLETE CAPTCHA" : "SIGN IN"}</button>
                </form>
                {error && <div style={{ color: "red", marginBottom: "10px" }}>{error}</div>}
                <div className="login-footer-links">
                    <a href="/register">Need an account ?</a>
                    <span>|</span>
                    <span
                        onClick={() => {
                            setResetEmail(email); // optional, if you want to prefill
                            setResetStatus("");
                            setShowResetPopup(true);
                        }}
                        style={{cursor: "pointer", textDecoration: "underline" }}
                    >
                        Forgot your password?
                    </span>
                </div>
            </div>
            {showResetPopup && (
                <div className="reset-popup">
                    <div className="reset-box">
                        <h3>Reset Your Password</h3>

                        <input
                            type="email"
                            className="login-input"
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                            placeholder="enter your email"
                        />
                        <div className="turnstile-wrapper">
                            <Turnstile
                                sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                                onVerify={async (token) => {
                                    setResetToken(token);
                                    try {
                                        const res = await axios.post(process.env.REACT_APP_API_URL+"/verify-turnstile", {
                                            token,
                                        });
                                        setResetVerified(res.data.success);
                                    } catch (err) {
                                        console.error("Reset Turnstile verification failed:", err);
                                        setResetVerified(false);
                                    }
                                }}
                            />
                        </div>
                        <p></p>
                        <button
                            className="signup"
                            onClick={async () => {
                                try {
                                    await handleResetPassword(auth, resetEmail);
                                    setResetStatus("Reset link sent.");
                                    setShowResetPopup(false);
                                } catch (err) {
                                    console.error(err);
                                    setResetStatus("Error sending reset link.");
                                }
                            }}
                            disabled={!resetVerified || !resetEmail}
                        >
                            {!resetVerified ? "Please complete CAPTCHA" : !resetEmail ? "Please enter email" : "Send Recovery Link"}
                        </button>
                        <p></p>
                        <button className="login" onClick={() => setShowResetPopup(false)}>Cancel</button>
                        {resetStatus && <p>{resetStatus}</p>}
                    </div>
                </div>
            )}
        </div>
    );
}

export default Login;